// src/components/ProformaInvoiceList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { Link } from "react-router-dom";

export default function ProformaInvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // server pagination metadata (DRF standard)
  const [count, setCount] = useState(null);
  const [nextUrl, setNextUrl] = useState(null);
  const [prevUrl, setPrevUrl] = useState(null);

  // local pagination controls
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // actions state
  const [downloadingId, setDownloadingId] = useState(null);
  const [registeringId, setRegisteringId] = useState(null);

  // selection (radio)
  const [selectedId, setSelectedId] = useState(null);

  // ui state
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  // --- BACKEND SEARCH ---
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef(null);
  const onChangeSearch = (val) => {
    setSearch(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val.trim());
      setPage(1); // reset page when search changes
    }, 500);
  };

  const totalPages = useMemo(() => {
    if (!count || !pageSize) return null;
    return Math.max(1, Math.ceil(count / pageSize));
  }, [count, pageSize]);

  const listUrl = useMemo(() => {
    const base = "documents/proforma-invoices/";
    const usp = new URLSearchParams();
    usp.set("page", String(page));
    usp.set("page_size", String(pageSize));
    if (debouncedSearch) usp.set("search", debouncedSearch);
    return `${base}?${usp.toString()}`;
  }, [page, pageSize, debouncedSearch]);

  const fetchPage = async (urlOverride = null) => {
    setLoading(true);
    setError("");
    try {
      const url = urlOverride ?? listUrl;
      const res = await axiosInstance.get(url);
      const data = res.data ?? {};

      if (Array.isArray(data.results)) {
        setInvoices(data.results);
        setCount(typeof data.count === "number" ? data.count : null);
        setNextUrl(data.next ?? null);
        setPrevUrl(data.previous ?? null);

        // If navigating via absolute next/prev URL, sync page state
        if (urlOverride) {
          try {
            const parsed = new URL(urlOverride, window.location.origin);
            const p = parsed.searchParams.get("page");
            if (p) setPage(Number(p));
          } catch (_) {}
        }
      } else if (Array.isArray(data)) {
        setInvoices(data);
        setCount(data.length);
        setNextUrl(null);
        setPrevUrl(null);
      } else {
        const results = data.results ?? [];
        setInvoices(Array.isArray(results) ? results : []);
        setCount(typeof data.count === "number" ? data.count : results.length);
        setNextUrl(data.next ?? null);
        setPrevUrl(data.previous ?? null);
      }
    } catch (err) {
      console.error(err);
      setError("خطا در دریافت فهرست پروفورماها.");
    } finally {
      setLoading(false);
      setSelectedId(null); // clear selection when page/search changes
    }
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUrl]);

  const fmtMoney = (v) =>
    typeof v === "number"
      ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : v ?? "-";

  const downloadPDF = async (invoiceId) => {
    if (!invoiceId) return;
    setError("");
    setSuccessMessage("");
    setDownloadingId(invoiceId);
    try {
      const response = await axiosInstance.get(
        `documents/proforma-invoice/${invoiceId}/pdf`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const fileURL = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = fileURL;
      a.download = `proforma_${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(fileURL);
      setSuccessMessage("دانلود PDF با موفقیت آغاز شد.");
    } catch (err) {
      console.error(err);
      setError("خطا در دانلود PDF.");
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadSelected = () => {
    if (!selectedId) {
      setError("ابتدا یک پروفورما را انتخاب کنید.");
      return;
    }
    downloadPDF(selectedId);
  };

  const registerOrder = async (invoiceId) => {
    setError("");
    setSuccessMessage("");
    setRegisteringId(invoiceId);
    try {
      await axiosInstance.post("documents/create-order/", {
        invoice_id: invoiceId,
      });
      setSuccessMessage("سفارش هماهنگ با موفقیت ثبت شد.");
    } catch (err) {
      console.error(err);
      setError("خطا در ثبت سفارش هماهنگ.");
    } finally {
      setRegisteringId(null);
    }
  };

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(""), 3000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // ---------- Pagination controls ----------
  const canPrev = totalPages ? page > 1 : Boolean(prevUrl);
  const canNext = totalPages ? page < totalPages : Boolean(nextUrl);

  const goFirst = () => totalPages && setPage(1);
  const goPrev = () => {
    if (totalPages) setPage((p) => Math.max(1, p - 1));
    else if (prevUrl) fetchPage(prevUrl);
  };
  const goNext = () => {
    if (totalPages) setPage((p) => p + 1);
    else if (nextUrl) fetchPage(nextUrl);
  };
  const goLast = () => totalPages && setPage(totalPages);

  const pageNumbers = useMemo(() => {
    if (!totalPages) return [];
    const windowSize = 7;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    if (end - start + 1 < windowSize) start = Math.max(1, end - windowSize + 1);
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const showingFrom = useMemo(() => {
    if (!count || !totalPages) return null;
    return (page - 1) * pageSize + 1;
  }, [page, pageSize, count, totalPages]);

  const showingTo = useMemo(() => {
    if (!count || !totalPages) return null;
    return Math.min(page * pageSize, count);
  }, [page, pageSize, count, totalPages]);

  return (
    <div dir="rtl" className="font-vazir mt-20 md:mt-24 px-5">
      {/* Top bar */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">فهرست پروفورماها</h1>
          <p className="text-gray-500 text-sm mt-1">
            جستجو سمت سرور، انتخاب با رادیو، دانلود تکی
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => onChangeSearch(e.target.value)}
              placeholder="جستجو در سرور…"
              className="w-full md:w-64 px-4 py-2 rounded-xl border focus:ring-2 focus:ring-blue-600"
            />
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/proforma-invoices/new"
              className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              ایجاد پروفورما
            </Link>
            <button
              type="button"
              onClick={downloadSelected}
              className="px-4 py-2 rounded-xl text-white bg-gray-700 border hover:bg-gray-800 transition disabled:opacity-60"
              disabled={loading || !invoices?.length || !selectedId}
              title="دانلود PDF سطر انتخاب‌شده"
            >
              دانلود انتخاب‌شده
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {successMessage && (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-green-700">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      {/* Card + table */}
      <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
        <div className="border-b px-4 md:px-6 py-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">پروفورماها</h2>

          {/* Page size */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">تعداد در صفحه</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="px-3 py-1 rounded-lg border"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="px-2 md:px-6 py-4 overflow-x-auto">
          {loading ? (
            <div className="py-10 text-center text-gray-500">
              در حال بارگذاری…
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              پروفورمایی یافت نشد.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="text-gray-600 border-b">
                <tr className="text-right">
                  <th className="py-3 px-2">انتخاب</th>
                  <th className="py-3 px-2">شناسه</th>
                  <th className="py-3 px-2">شماره پروفورما</th>
                  <th className="py-3 px-2">مبلغ کل</th>
                  <th className="py-3 px-2">هزینه حمل</th>
                  <th className="py-3 px-2">واحد پول</th>
                  <th className="py-3 px-2">تاریخ</th>
                  <th className="py-3 px-2">دانلود</th>
                  <th className="py-3 px-2">جزئیات</th>
                  <th className="py-3 px-2">ثبت سفارش هماهنگ</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b last:border-0">
                    <td className="py-3 px-2">
                      <input
                        type="radio"
                        name="selected-proforma"
                        checked={selectedId === inv.id}
                        onChange={() => setSelectedId(inv.id)}
                        className="h-4 w-4"
                        aria-label={`انتخاب پروفورما ${
                          inv.proforma_invoice_number ?? inv.id
                        }`}
                      />
                    </td>
                    <td className="py-3 px-2">{inv.proforma_invoice_id}</td>
                    <td className="py-3 px-2">{inv.proforma_invoice_number}</td>
                    <td className="py-3 px-2">{fmtMoney(inv.total_amount)}</td>
                    <td className="py-3 px-2">
                      {fmtMoney(inv.proforma_freight_charges)}
                    </td>
                    <td className="py-3 px-2">
                      {inv.proforma_invoice_currency}
                    </td>
                    <td className="py-3 px-2">{inv.proforma_invoice_date}</td>
                    <td className="py-3 px-2">
                      <button
                        className="px-3 py-1 rounded-xl border hover:bg-gray-50 transition disabled:opacity-60"
                        onClick={() => downloadPDF(inv.id)}
                        disabled={downloadingId === inv.id}
                      >
                        {downloadingId === inv.id
                          ? "در حال دانلود…"
                          : "دانلود PDF"}
                      </button>
                    </td>
                    <td className="py-3 px-2">
                      <Link
                        className="text-blue-600 hover:underline"
                        to={`/proforma-invoices/details/${inv.proforma_invoice_id}`}
                      >
                        مشاهده
                      </Link>
                    </td>
                    <td className="py-3 px-2">
                      <button
                        className="px-3 py-1 rounded-xl border hover:bg-gray-50 transition disabled:opacity-60"
                        onClick={() => registerOrder(inv.id)}
                        disabled={registeringId === inv.id}
                      >
                        {registeringId === inv.id ? "در حال ثبت…" : "ثبت سفارش"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Footer: range + pagination */}
          <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            {count !== null && totalPages && (
              <div className="text-sm text-gray-600">
                نمایش {showingFrom} تا {showingTo} از {count} رکورد
              </div>
            )}

            <div className="flex items-center gap-1 md:gap-2">
              <button
                className="px-3 py-1 rounded-lg border hover:bg-gray-50 transition disabled:opacity-50"
                onClick={goFirst}
                disabled={!canPrev || !totalPages}
                title="اولین صفحه"
              >
                «
              </button>
              <button
                className="px-3 py-1 rounded-lg border hover:bg-gray-50 transition disabled:opacity-50"
                onClick={goPrev}
                disabled={!canPrev}
                title="قبلی"
              >
                قبلی
              </button>

              {totalPages ? (
                <>
                  {pageNumbers[0] > 1 && (
                    <button
                      className="px-3 py-1 rounded-lg border hover:bg-gray-50 transition"
                      onClick={() => setPage(1)}
                    >
                      1
                    </button>
                  )}
                  {pageNumbers[0] > 2 && <span className="px-2">…</span>}
                  {pageNumbers.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={
                        "px-3 py-1 rounded-lg border transition " +
                        (p === page
                          ? "bg-blue-600 text-white border-blue-600"
                          : "hover:bg-gray-50")
                      }
                    >
                      {p}
                    </button>
                  ))}
                  {pageNumbers.at(-1) < totalPages - 1 && (
                    <span className="px-2">…</span>
                  )}
                  {pageNumbers.at(-1) < totalPages && (
                    <button
                      className="px-3 py-1 rounded-lg border hover:bg-gray-50 transition"
                      onClick={() => setPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  )}
                </>
              ) : null}

              <button
                className="px-3 py-1 rounded-lg border hover:bg-gray-50 transition disabled:opacity-50"
                onClick={goNext}
                disabled={!canNext}
                title="بعدی"
              >
                بعدی
              </button>
              <button
                className="px-3 py-1 rounded-lg border hover:bg-gray-50 transition disabled:opacity-50"
                onClick={goLast}
                disabled={!canNext || !totalPages}
                title="آخرین صفحه"
              >
                »
              </button>
            </div>

            {/* Fallback prev/next (no count) */}
            {!totalPages && (prevUrl || nextUrl) && (
              <div className="flex items-center justify-between gap-2 md:ml-auto">
                <button
                  className="px-4 py-2 rounded-xl border hover:bg-gray-50 transition disabled:opacity-60"
                  onClick={() => fetchPage(prevUrl)}
                  disabled={!prevUrl}
                >
                  قبلی
                </button>
                <button
                  className="px-4 py-2 rounded-xl border hover:bg-gray-50 transition disabled:opacity-60"
                  onClick={() => fetchPage(nextUrl)}
                  disabled={!nextUrl}
                >
                  بعدی
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
